<!-- 宠物排行榜：学期分榜 / 进步榜 / 班级榜（Tab）。最后更新：2026-11-04 -->
<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" :type="0"></DepartmentPane>
    </section>
    <section class="dp-right pet-rank-page">
      <div class="page-head">
        <p class="dp-dept-hint">
          <span class="dp-dept-hint__label">排行范围</span>
          <span class="dp-dept-hint__value">{{ departmentName || '未选择' }}</span>
        </p>
        <div class="filter-bar">
          <el-select v-model="query.semesterId" size="small" placeholder="学期" class="filter-bar__semester" @change="handleSemesterChange">
            <el-option v-for="s in semesters" :key="s.semesterId" :value="s.semesterId" :label="s.semesterName + (s.isCurrent ? '（当前）' : '')" />
          </el-select>
          <el-date-picker
            v-model="query.viewDate"
            type="date"
            size="small"
            value-format="yyyy-MM-dd"
            placeholder="统计日期"
            :clearable="false"
            :picker-options="datePickerOptions"
            class="filter-bar__date"
            @change="load" />
          <el-radio-group v-if="activeTab !== 'score'" v-model="query.progressDays" size="small" @change="load">
            <el-radio-button :label="7">近 7 天</el-radio-button>
            <el-radio-button :label="14">近 14 天</el-radio-button>
            <el-radio-button :label="30">近 30 天</el-radio-button>
          </el-radio-group>
          <el-select v-if="activeTab !== 'class'" v-model="query.top" size="small" class="filter-bar__top" @change="load">
            <el-option :value="10" label="前 10 名" />
            <el-option :value="20" label="前 20 名" />
            <el-option :value="50" label="前 50 名" />
          </el-select>
          <el-radio-group v-model="query.claimStatus" size="small" @change="load">
            <el-radio-button label="">全部学生</el-radio-button>
            <el-radio-button :label="1">已领取</el-radio-button>
            <el-radio-button :label="0">未领取</el-radio-button>
          </el-radio-group>
          <el-button type="primary" size="small" icon="el-icon-refresh" :loading="loading" @click="load">刷新</el-button>
          <el-button size="small" type="warning" plain icon="el-icon-download" :loading="exporting" @click="exportRanking">导出 Excel</el-button>
        </div>
      </div>

      <div class="page-body" v-loading="loading">
        <div class="panel panel--tabs">
          <el-tabs v-model="activeTab" class="rank-tabs" @tab-click="handleTabClick">
            <el-tab-pane name="score" lazy>
              <span slot="label">学期分榜<span class="tab-count">{{ studentRanks.length }}</span></span>
              <div class="tab-shell">
                <div class="tab-toolbar">
                  <span class="panel__desc">{{ rank.semesterName || '—' }} · 截至 {{ rank.viewDate || query.viewDate }}</span>
                </div>
                <div class="tab-scroll">
                  <div v-if="studentRanks.length" class="rank-list">
                    <div v-for="item in studentRanks" :key="'s' + item.studentId" :class="['rank-row', topClass(item.rank)]">
                      <span class="rank-no">{{ item.rank }}</span>
                      <span class="rank-face">
                        <oss-media-thumbs v-if="item.headPhotoImage" :media="item.headPhotoImage" :max="1" thumb-width="34px" thumb-height="34px" />
                        <span v-else class="rank-initial">{{ (item.studentName || '?').slice(-1) }}</span>
                      </span>
                      <span class="rank-who">
                        <span class="rank-name" :title="item.studentName">{{ item.studentName }}</span>
                        <span class="rank-sub" :title="item.departmentName">{{ item.departmentName }}</span>
                      </span>
                      <span class="rank-pet">
                        <span class="rank-pet__face">
                          <oss-media-thumbs v-if="item.hasPet && item.avatarUrl" :media="item.avatarUrl" :max="1" thumb-width="28px" thumb-height="28px" />
                        </span>
                        <span class="level-badge">Lv.{{ item.levelIndex || 1 }}</span>
                      </span>
                      <div class="rank-bar"><span :style="{ width: barWidth(item.semesterScore, maxStudentScore) }"></span></div>
                      <span class="rank-value">{{ formatScore(item.semesterScore) }}<small>分</small></span>
                    </div>
                  </div>
                  <div v-else class="panel-empty panel-empty--rank">暂无数据</div>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane name="progress" lazy>
              <span slot="label">进步榜<span class="tab-count">{{ progressRanks.length }}</span></span>
              <div class="tab-shell">
                <div class="tab-toolbar">
                  <span class="panel__desc">{{ rank.progressFromDate ? nextDay(rank.progressFromDate) : '' }} 至 {{ rank.viewDate || query.viewDate }} 学期分增长</span>
                </div>
                <div class="tab-scroll">
                  <div v-if="progressRanks.length" class="rank-list">
                    <div v-for="item in progressRanks" :key="'p' + item.studentId" :class="['rank-row', topClass(item.rank)]">
                      <span class="rank-no">{{ item.rank }}</span>
                      <span class="rank-face">
                        <oss-media-thumbs v-if="item.headPhotoImage" :media="item.headPhotoImage" :max="1" thumb-width="34px" thumb-height="34px" />
                        <span v-else class="rank-initial">{{ (item.studentName || '?').slice(-1) }}</span>
                      </span>
                      <span class="rank-who">
                        <span class="rank-name" :title="item.studentName">{{ item.studentName }}</span>
                        <span class="rank-sub" :title="item.departmentName">{{ item.departmentName }}</span>
                      </span>
                      <span class="rank-pet">
                        <span class="rank-pet__face">
                          <oss-media-thumbs v-if="item.hasPet && item.avatarUrl" :media="item.avatarUrl" :max="1" thumb-width="28px" thumb-height="28px" />
                        </span>
                        <span class="level-badge">Lv.{{ item.levelIndex || 1 }}</span>
                      </span>
                      <div class="rank-bar is-up"><span :style="{ width: barWidth(item.gainScore, maxProgressGain) }"></span></div>
                      <span class="rank-value is-up">+{{ formatScore(item.gainScore) }}<small>分</small></span>
                    </div>
                  </div>
                  <div v-else class="panel-empty panel-empty--rank">
                    <i class="el-icon-data-line"></i>
                    <span>该时间段内没有学生学期分增长</span>
                  </div>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane name="class" lazy>
              <span slot="label">班级榜<span class="tab-count">{{ classRanks.length }}</span></span>
              <div class="tab-shell">
                <div class="tab-toolbar">
                  <span class="panel__desc">按平均学期分排名，同分同名次 · 共 {{ classRanks.length }} 个班级</span>
                </div>
                <div class="tab-scroll tab-scroll--table">
                  <el-table :data="pagedClassRanks" size="small" class="class-table" height="100%" row-class-name="is-clickable" @row-click="openClass">
                    <el-table-column label="名次" width="70" align="center">
                      <template slot-scope="{ row }"><span :class="['class-rank', topClass(row.rank)]">{{ row.rank }}</span></template>
                    </el-table-column>
                    <el-table-column prop="departmentName" label="班级" min-width="120" align="center" show-overflow-tooltip />
                    <el-table-column label="平均学期分" min-width="200" align="center">
                      <template slot-scope="{ row }">
                        <div class="score-cell">
                          <div class="bar-track"><span class="bar-fill" :style="{ width: barWidth(row.avgSemesterScore, maxClassScore) }"></span></div>
                          <span class="score-cell__text">{{ row.avgSemesterScore }}</span>
                        </div>
                      </template>
                    </el-table-column>
                    <el-table-column label="平均等级" width="100" align="center">
                      <template slot-scope="{ row }">Lv.{{ row.avgLevel }}</template>
                    </el-table-column>
                    <el-table-column :label="`近 ${query.progressDays} 天人均增长`" width="140" align="center">
                      <template slot-scope="{ row }">
                        <span :class="['gain', row.avgGainScore > 0 ? 'is-up' : row.avgGainScore < 0 ? 'is-down' : '']">{{ signedScore(row.avgGainScore) }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column label="领取率" width="140" align="center">
                      <template slot-scope="{ row }"><span class="muted">{{ row.claimedCount }}/{{ row.studentCount }} · {{ percent(row.claimedCount, row.studentCount) }}%</span></template>
                    </el-table-column>
                  </el-table>
                </div>
                <div v-if="classRanks.length" class="tab-foot">
                  <span class="class-pager__hint"><i class="el-icon-info"></i>点击班级行可查看学生明细</span>
                  <el-pagination
                    background
                    layout="total, sizes, prev, pager, next, jumper"
                    :total="classRanks.length"
                    :current-page.sync="classPage"
                    :page-size.sync="classPageSize"
                    :page-sizes="[10, 20, 50]"
                    @size-change="classPage = 1" />
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>

      <pet-student-list-dialog
        :visible.sync="drill.visible"
        :title="drill.title"
        :school-id="schoolId"
        :filters="drill.filters"
        :tags="drill.tags"
        :semesters="semesters" />
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue'
import PetStudentListDialog from './components/PetStudentListDialog.vue'
import { getPetRanking, exportPetRanking, getPetSemesterList } from '@/api/modules/pet'
import { resolveActiveSchoolId, treeNodeDeptId, deptIdsEqual } from '@/utils/deptCascaderProps'

/** 日期格式化为 yyyy-MM-dd */
function formatDate (date) {
  const d = new Date(date)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'PetRanking',
  components: { DepartmentPane, PetStudentListDialog },
  data () {
    return {
      schoolId: 0,
      departmentId: 0,
      departmentName: '',
      loading: false,
      exporting: false,
      semesters: [],
      query: { semesterId: null, viewDate: formatDate(new Date()), progressDays: 7, top: 20, claimStatus: '' },
      rank: {},
      activeTab: 'score',
      classPage: 1,
      classPageSize: 10,
      drill: { visible: false, title: '', filters: {}, tags: [] }
    }
  },
  computed: {
    currentSemester () {
      return this.semesters.find(x => x.semesterId === this.query.semesterId) || null
    },
    datePickerOptions () {
      const semester = this.currentSemester
      const today = formatDate(new Date())
      return {
        disabledDate: date => {
          const value = formatDate(date)
          if (value > today) return true
          if (!semester) return false
          return value < String(semester.startDate || '').slice(0, 10) || value > String(semester.endDate || '').slice(0, 10)
        }
      }
    },
    studentRanks () {
      return this.rank.studentRanks || []
    },
    progressRanks () {
      return this.rank.progressRanks || []
    },
    classRanks () {
      return this.rank.classRanks || []
    },
    pagedClassRanks () {
      const start = (this.classPage - 1) * this.classPageSize
      return this.classRanks.slice(start, start + this.classPageSize)
    },
    maxClassScore () {
      return Math.max(1, ...this.classRanks.map(x => Number(x.avgSemesterScore) || 0))
    },
    maxStudentScore () {
      return Math.max(1, ...this.studentRanks.map(x => Number(x.semesterScore) || 0))
    },
    maxProgressGain () {
      return Math.max(1, ...this.progressRanks.map(x => Number(x.gainScore) || 0))
    }
  },
  methods: {
    async treeNodeClick (data) {
      this.departmentId = treeNodeDeptId(data)
      this.departmentName = (data && (data.label || data.Name)) || ''
      const schoolId = resolveActiveSchoolId(this)
      if (schoolId && !deptIdsEqual(schoolId, this.schoolId)) {
        this.schoolId = schoolId
        await this.loadSemesters()
      }
      this.load()
    },
    async loadSemesters () {
      if (!this.schoolId) return
      const res = await getPetSemesterList({ schoolId: this.schoolId })
      this.semesters = (res.data && res.data.response) || []
      const current = this.semesters.find(x => x.isCurrent) || this.semesters[0]
      this.query.semesterId = current ? current.semesterId : null
      this.clampViewDate()
    },
    clampViewDate () {
      const semester = this.currentSemester
      const today = formatDate(new Date())
      if (!semester) {
        this.query.viewDate = today
        return
      }
      const start = String(semester.startDate || '').slice(0, 10)
      const end = String(semester.endDate || '').slice(0, 10)
      const latest = end && end < today ? end : today
      const value = this.query.viewDate
      if (!value || value > latest || (start && value < start)) this.query.viewDate = latest
    },
    handleSemesterChange () {
      this.clampViewDate()
      this.load()
    },
    async load () {
      if (!this.schoolId || !this.departmentId) return
      this.loading = true
      try {
        const res = await getPetRanking({
          schoolId: this.schoolId,
          departmentId: this.departmentId,
          semesterId: this.query.semesterId || null,
          viewDate: this.query.viewDate || null,
          progressDays: this.query.progressDays,
          top: this.query.top,
          claimStatus: this.query.claimStatus === '' ? null : this.query.claimStatus
        })
        if (res.data && res.data.success) {
          const data = res.data.response || {}
          this.rank = data
          this.classPage = 1
          await this.prefetchActiveTabMedia()
        } else {
          this.$message.error((res.data && res.data.msg) || '加载失败')
        }
      } finally {
        this.loading = false
      }
    },
    /** 切换 Tab 时按需预取当前榜头像，避免两榜同时打 OSS */
    handleTabClick () {
      this.$nextTick(() => this.prefetchActiveTabMedia())
    },
    async prefetchActiveTabMedia () {
      if (!this.prefetchListOssMedia) return
      if (this.activeTab === 'score') {
        await this.prefetchListOssMedia(this.studentRanks, ['headPhotoImage', 'avatarUrl'])
      } else if (this.activeTab === 'progress') {
        await this.prefetchListOssMedia(this.progressRanks, ['headPhotoImage', 'avatarUrl'])
      }
    },
    /** 导出当前筛选条件下的三类排行榜。 */
    async exportRanking () {
      if (!this.schoolId || !this.departmentId) return this.$message.warning('请先在左侧选择部门')
      this.exporting = true
      try {
        const res = await exportPetRanking({
          schoolId: this.schoolId,
          departmentId: this.departmentId,
          semesterId: this.query.semesterId || null,
          viewDate: this.query.viewDate || null,
          progressDays: this.query.progressDays,
          top: this.query.top,
          claimStatus: this.query.claimStatus === '' ? null : this.query.claimStatus
        })
        if (!res.data || !res.data.size) return this.$message.error('没有可导出的数据')
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = `宠物排行榜_${this.query.viewDate || ''}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    },
    /** 点击班级榜行，查看该班学生（带当前学期、日期和领取状态筛选） */
    openClass (row) {
      if (!this.schoolId) return
      const claimText = this.query.claimStatus === 1 ? '已领取' : this.query.claimStatus === 0 ? '未领取' : ''
      this.drill = {
        visible: true,
        title: `${row.departmentName} 学生`,
        filters: {
          departmentId: row.departmentId,
          semesterId: this.query.semesterId || null,
          viewDate: this.query.viewDate || null,
          claimStatus: this.query.claimStatus === '' ? null : this.query.claimStatus
        },
        tags: [row.departmentName, this.rank.semesterName, `截至 ${this.query.viewDate}`, claimText].filter(Boolean)
      }
    },
    topClass (rank) {
      return rank >= 1 && rank <= 3 ? 'is-top' + rank : ''
    },
    nextDay (value) {
      const [y, m, d] = String(value).split('-').map(Number)
      return formatDate(new Date(y, m - 1, d + 1))
    },
    formatScore (value) {
      const num = Number(value) || 0
      return Number.isInteger(num) ? num : num.toFixed(1)
    },
    signedScore (value) {
      const num = Number(value) || 0
      return num > 0 ? `+${this.formatScore(num)}` : `${this.formatScore(num)}`
    },
    percent (value, total) {
      return total ? Math.round((Number(value) || 0) / total * 100) : 0
    },
    barWidth (value, max) {
      if (!value || value <= 0 || !max) return '0%'
      return Math.max(2, (value / max) * 100) + '%'
    }
  }
}
</script>

<style scoped>
.pet-rank-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}
.page-head { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
.page-head .dp-dept-hint { margin: 0; }
.filter-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.filter-bar__semester { width: 190px; }
.filter-bar__date { width: 140px !important; }
.filter-bar__top { width: 110px; }
.page-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 16px 12px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.panel--tabs { flex: 1 1 auto; min-height: 0; overflow: hidden; }
.panel__desc { font-size: 12px; color: #64748b; }
.panel-empty { padding: 28px 0; text-align: center; color: #94a3b8; font-size: 13px; }
.panel-empty--rank {
  min-height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 0;
}
.panel-empty--rank i { font-size: 32px; color: #cbd5e1; }

.rank-tabs { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; height: 100%; }
.rank-tabs /deep/ .el-tabs__header { margin: 0 0 8px; flex-shrink: 0; }
.rank-tabs /deep/ .el-tabs__nav-wrap::after { height: 1px; background-color: #f1f5f9; }
.rank-tabs /deep/ .el-tabs__item { height: 40px; line-height: 40px; font-weight: 500; color: #64748b; }
.rank-tabs /deep/ .el-tabs__item.is-active { color: #2563eb; font-weight: 600; }
.rank-tabs /deep/ .el-tabs__active-bar { background-color: #2563eb; }
.rank-tabs /deep/ .el-tabs__content { flex: 1 1 auto; min-height: 0; overflow: hidden; }
.rank-tabs /deep/ .el-tab-pane { height: 100%; }
.tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 18px;
}
.rank-tabs /deep/ .el-tabs__item.is-active .tab-count { background: #eff6ff; color: #2563eb; }

.tab-shell { height: 100%; min-height: 0; display: flex; flex-direction: column; }
.tab-toolbar { flex: 0 0 auto; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.tab-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  scrollbar-width: thin;
}
.tab-scroll--table { overflow: hidden; padding-right: 0; }
.tab-scroll::-webkit-scrollbar { width: 6px; }
.tab-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; }
.tab-scroll::-webkit-scrollbar-track { background: #f8fafc; border-radius: 999px; }
.tab-foot {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f1f5f9;
}
.tab-foot /deep/ .el-pagination { margin: 0 0 0 auto !important; }

.rank-list { display: flex; flex-direction: column; gap: 8px; }
.rank-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: #fff;
  transition: background-color .2s ease, border-color .2s ease;
}
.rank-row:hover { background: #f8fafc; }
.rank-row.is-top1 { background: #fffbeb; border-color: #fde68a; }
.rank-row.is-top2 { background: #f8fafc; border-color: #e2e8f0; }
.rank-row.is-top3 { background: #fff7ed; border-color: #fed7aa; }
.rank-no {
  flex: 0 0 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.is-top1 .rank-no { background: #f59e0b; color: #fff; }
.is-top2 .rank-no { background: #94a3b8; color: #fff; }
.is-top3 .rank-no { background: #c2410c; color: #fff; }
.rank-face {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rank-face /deep/ .oss-media-thumbs {
  width: 34px;
  height: 34px;
  display: block;
  flex-wrap: nowrap;
  overflow: hidden;
  gap: 0;
}
.rank-face /deep/ .oss-media-thumbs-more { display: none !important; }
.rank-face /deep/ .oss-media-thumbs img {
  width: 34px !important;
  height: 34px !important;
  border-radius: 6px;
  object-fit: cover;
  display: block;
}
.rank-initial {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 600;
}
.rank-who {
  flex: 0 1 180px;
  width: 180px;
  min-width: 120px;
  max-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}
.rank-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-sub {
  font-size: 12px;
  line-height: 1.3;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-pet {
  flex: 0 0 84px;
  width: 84px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.rank-pet__face {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  position: relative;
  overflow: hidden;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.rank-pet__face /deep/ .oss-media-thumbs {
  width: 28px;
  height: 28px;
  display: block;
  flex-wrap: nowrap;
  overflow: hidden;
  gap: 0;
}
.rank-pet__face /deep/ .oss-media-thumbs-more { display: none !important; }
.rank-pet__face /deep/ .oss-media-thumbs img {
  width: 28px !important;
  height: 28px !important;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.rank-bar {
  flex: 1 1 auto;
  position: relative;
  height: 8px;
  min-width: 80px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;
}
.rank-bar span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #60a5fa, #2563eb);
  transition: width .3s ease;
}
.rank-bar.is-up span { background: linear-gradient(90deg, #86efac, #16a34a); }
.rank-value {
  flex: 0 0 64px;
  width: 64px;
  text-align: right;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.rank-value small { font-size: 12px; font-weight: 400; color: #64748b; margin-left: 2px; }
.rank-value.is-up { color: #15803d; }
.level-badge {
  flex-shrink: 0;
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.class-table { width: 100%; }
.class-table /deep/ .el-table__row:hover > td { background: #f5f9ff !important; }
.class-pager__hint { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; }
.class-pager__hint i { color: #cbd5e1; }
.class-table /deep/ .el-table__row.is-clickable { cursor: pointer; }
.class-rank { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; background: #f1f5f9; color: #475569; font-weight: 600; font-variant-numeric: tabular-nums; }
.class-rank.is-top1 { background: #f59e0b; color: #fff; }
.class-rank.is-top2 { background: #94a3b8; color: #fff; }
.class-rank.is-top3 { background: #c2410c; color: #fff; }
.score-cell { display: flex; align-items: center; gap: 10px; }
.score-cell__text { flex-shrink: 0; min-width: 44px; text-align: right; font-weight: 600; color: #0f172a; font-variant-numeric: tabular-nums; }
.bar-track { position: relative; flex: 1; height: 8px; border-radius: 999px; background: #f1f5f9; overflow: hidden; }
.bar-fill { position: absolute; left: 0; top: 0; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #60a5fa, #2563eb); transition: width .3s ease; }
.gain { font-weight: 600; font-variant-numeric: tabular-nums; color: #64748b; }
.gain.is-up { color: #15803d; }
.gain.is-down { color: #b45309; }
.muted { color: #64748b; font-size: 12px; }

@media screen and (min-width: 769px) {
  .dp-right.pet-rank-page { overflow: hidden; }
}
</style>
